import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { seoArticles } from "@/mocks/articles";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { SITE_URL } from "@/constants";

export default function DynamicArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);

  const articleUrl = article ? `${SITE_URL}/article/${article.slug}` : "";

  const articleSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.image,
      "author": {
        "@type": "Organization",
        "name": "OOPBuy Spreadsheet",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "OOPBuy Spreadsheet",
        "url": SITE_URL
      },
      "datePublished": article.date,
      "dateModified": article.date,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      },
      "url": articleUrl,
      "wordCount": String(article.sections.reduce((count, s) => count + s.paragraphs.join(" ").split(" ").length, 0)),
      "inLanguage": "en-US"
    } as Record<string, unknown>;
  }, [article, articleUrl]);

  const breadcrumbSchema = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Articles",
          "item": `${SITE_URL}/#articles-section`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": article.title,
          "item": articleUrl
        }
      ]
    } as Record<string, unknown>;
  }, [article, articleUrl]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | oopbuyspreadsheets.lat`;
    } else {
      document.title = "Article Not Found | oopbuyspreadsheets.lat";
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <Link to="/" className="text-sm text-accent-500 hover:text-accent-600 transition-colors">
            <i className="ri-arrow-left-line mr-1 align-middle"></i>Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {articleSchema && <JsonLd data={articleSchema} />}
      {breadcrumbSchema && <JsonLd data={breadcrumbSchema} />}

      {/* Article Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-background-200/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-lg font-bold text-foreground-900 font-heading tracking-tight">
              OOPBuy<span className="text-accent-500">Spreadsheet</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-accent-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1 align-middle"></i>Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm px-4 py-2 rounded-full bg-accent-500 text-white hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Article Header */}
        <header className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <nav className="text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-accent-500 transition-colors">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/#articles-section" className="hover:text-accent-500 transition-colors">Articles</Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-700">{article.title}</span>
              </nav>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500">
                <span>Published: {article.date}</span>
                <span>&middot;</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article className="py-12 md:py-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Main Content */}
              <div className="lg:flex-1 max-w-none lg:max-w-[720px]">
                <div className="prose-custom space-y-8 text-sm text-foreground-700 leading-relaxed">
                  {article.sections.map((section, idx) => {
                    const HeadingTag = section.headingLevel as keyof JSX.IntrinsicElements;
                    const headingClass = section.headingLevel === 'h2'
                      ? "text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4"
                      : "text-base font-semibold text-foreground-800 mt-6 mb-2";

                    return (
                      <section key={idx}>
                        <HeadingTag className={headingClass}>{section.heading}</HeadingTag>
                        {section.paragraphs.map((p, pIdx) => (
                          <p key={pIdx} className="mb-4">{p}</p>
                        ))}
                        {section.listItems && (
                          <ul className="space-y-2 list-disc pl-5 mb-4">
                            {section.listItems.map((item, lIdx) => (
                              <li key={lIdx}>{item}</li>
                            ))}
                          </ul>
                        )}
                      </section>
                    );
                  })}
                </div>

                {/* Article CTA */}
                <div className="mt-12 p-6 md:p-8 bg-accent-100/60 rounded-lg border border-accent-200/50">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">
                    Ready to Start Your Research?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-5 leading-relaxed">
                    Access the complete OOPBuy spreadsheet database and begin making data-driven product sourcing decisions today.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-6 py-2.5 rounded-full bg-foreground-800 text-white text-sm font-medium hover:bg-foreground-900 transition-all text-center whitespace-nowrap cursor-pointer"
                    >
                      Buy It Now <i className="ri-arrow-right-up-line ml-1 align-middle"></i>
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-2.5 rounded-full border-2 border-foreground-200 text-foreground-700 text-sm font-medium hover:border-accent-400 hover:text-accent-600 transition-all text-center whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line mr-1 align-middle"></i>Back to Home
                    </Link>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="sticky top-24 space-y-6">
                  <div className="w-full h-[380px] rounded-xl overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.imageAlt}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="bg-white border border-background-200/70 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-foreground-800 mb-3">Quick Links</h4>
                    <ul className="space-y-2">
                      <li>
                        <Link to="/" className="text-xs text-accent-600 hover:text-accent-700 transition-colors">
                          <i className="ri-home-line mr-1 align-middle"></i>Home Page
                        </Link>
                      </li>
                      <li>
                        <Link to="/#articles-section" className="text-xs text-accent-600 hover:text-accent-700 transition-colors">
                          <i className="ri-article-line mr-1 align-middle"></i>All Articles
                        </Link>
                      </li>
                      <li>
                        <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap">
                          <i className="ri-external-link-line mr-1 align-middle"></i>Main Website
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}